import { DeleteOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, List, message, Modal, Select, Space, Tag } from 'antd';
import { FC, useMemo, useState } from 'react';

import {
  FactoryManagerRow,
  useAssignFactoryManager,
  useGetFactoryManagers,
  useUnassignFactoryManager,
} from '@/modules/factory/hooks/useFactoryApi';
import { useGetHRList } from '@/modules/hr-management/hooks/useGetHRList';

// Phần II §6.4 — QLĐ scope. Each Factory has 0..N assigned managers; the
// QLĐ user only sees Factory data they've been added to.
//
// V1.4 D2.3: list is now persistent from the BE (D1.4 endpoint
// GET /factory/:id/managers) instead of session-local — re-opening the
// modal shows everyone already assigned. Account_type filter limits the
// Select to QLĐ-eligible users (account_type MANAGER) so an NLĐ can't be
// gifted scope by accident.
interface IProps {
  readonly factoryId: string;
  readonly factoryName: string;
  readonly visible: boolean;
  readonly onClose: () => void;
}

interface EmployeeRow {
  id: string;
  name: string;
  phone?: string;
  account_type: string;
}

const AssignManagerModal: FC<IProps> = ({ factoryId, factoryName, visible, onClose }) => {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const queryClient = useQueryClient();

  // Use the standard hr-management hook; it's already used elsewhere so we
  // ride its query-key for de-dup. We request a wide page since we filter
  // client-side by account_type below — the BE doesn't yet expose that as
  // a query param.
  const { data: employeesPaging } = useGetHRList('page=1&limit=200');

  const { data: assignments = [] } = useGetFactoryManagers(visible ? factoryId : undefined);

  function invalidate() {
    queryClient.invalidateQueries(['factory-managers', factoryId]);
  }

  const assign = useAssignFactoryManager({
    onSuccess: () => {
      setSelectedUserId('');
      invalidate();
    },
  });

  const unassign = useUnassignFactoryManager({
    onSuccess: invalidate,
  });

  const employeeOptions = useMemo(() => {
    const rows =
      (employeesPaging as unknown as { rows?: EmployeeRow[] })?.rows ?? [];
    const assignedIds = new Set(assignments.map((a) => a.user_id));

    return rows
      // V1.4 D4: filter to MANAGER account_type only so the Select can't
      // accidentally hand QLĐ scope to an NLĐ / Admin / Owner.
      .filter((e) => e.account_type === 'MANAGER')
      .filter((e) => !assignedIds.has(e.id))
      .map((e) => ({
        label: `${e.name}${e.phone ? ' • ' + e.phone : ''}`,
        value: e.id,
      }));
  }, [employeesPaging, assignments]);

  function handleAssign() {
    if (!selectedUserId) {
      message.warning('Chọn user để gán');

      return;
    }
    assign.mutate({ factory_id: factoryId, user_id: selectedUserId });
  }

  return (
    <Modal
      footer={<Button onClick={onClose}>Đóng</Button>}
      onCancel={onClose}
      open={visible}
      title={`Phân QLĐ — ${factoryName}`}
      width={640}
    >
      <Space.Compact style={{ display: 'flex', marginBottom: 16, width: '100%' }}>
        <Select
          onChange={setSelectedUserId}
          options={employeeOptions}
          placeholder="Chọn QLĐ để gán..."
          showSearch
          style={{ flex: 1 }}
          value={selectedUserId || undefined}
        />
        <Button loading={assign.isLoading} onClick={handleAssign} type="primary">
          Gán
        </Button>
      </Space.Compact>

      <List<FactoryManagerRow>
        bordered
        dataSource={assignments}
        locale={{ emptyText: 'Chưa có QLĐ nào được gán cho nhà máy này.' }}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Button
                danger
                icon={<DeleteOutlined rev="" />}
                key="remove"
                loading={unassign.isLoading}
                onClick={() =>
                  unassign.mutate({ factory_id: factoryId, user_id: item.user_id })
                }
                size="small"
              >
                Bỏ gán
              </Button>,
            ]}
          >
            <Space direction="vertical" size={0}>
              <Space>
                <strong>{item.name || '(không rõ tên)'}</strong>
                {item.phone && <Tag color="blue">{item.phone}</Tag>}
              </Space>
              {item.assigned_at && (
                <small style={{ color: '#999' }}>
                  Gán lúc {new Date(item.assigned_at).toLocaleString('vi-VN')}
                </small>
              )}
            </Space>
          </List.Item>
        )}
      />
    </Modal>
  );
};

export { AssignManagerModal };
